import {
  Action,
  AnyAction,
  applyMiddleware,
  combineReducers,
  compose,
  createStore as _createStore,
  Reducer,
} from 'redux';
import { geoSwitcherReducer } from '../../reducers';
import { IApplicationState, IReduxStore, TReduxActions } from '../../types/redux';
import thunk, { ThunkDispatch } from 'redux-thunk';
import { IApplicationContext } from '../../types/applicationContext';
import { IJsonQuery } from '../jsonQuery';
import {
  setJsonQueryReducer,
  setDealTypeReducer,
  selectOfferTypeReducer,
  setPriceReducer,
  selectGeoReducer,
  clearGeoReducer,
  clearReducer,
  metaReducer,
} from '../../reducers/filters';
import { IFiltersState } from '../../types/filters';
import { clientErrorsReducer } from '../../reducers/filters/clientErrors';
import { geocodeReducer } from '../../reducers/filters/geocode';
import { geoSearchReducer } from '../../reducers/filters/geoSearch';
import { suggestionsReducer } from '../../reducers/filters/suggestions';
import { oldReducers } from '../../reducers/reducers';
import { selectAllOfferTypesReducer } from '../../reducers/filters/jsonQuery/selectAllOfferTypes';
import { setRegionReducer } from '../../reducers/filters/jsonQuery/setRegion';
import { setVillageReducer } from '../../reducers/filters/jsonQuery/setVillage';
import { setBSCentersReducer } from '../../reducers/filters/jsonQuery/setBSCenters';
import { setBuilderReducer } from '../../reducers/filters/jsonQuery/setBuilder';
import { setCoworkingIdReducer } from '../../reducers/filters/jsonQuery/setCoworkingId';
import { experimentsReducer } from '../../reducers/experiments';
import { adfoxReducer } from '../../reducers/adfox';
import { trackingReducer } from '../../reducers/tracking';
import { createSimpleReducer } from './createSimpleReducer';
import { IUser } from '../../types/user';

let storeReminder: IReduxStore;

interface IThunkExt {
  dispatch: ThunkDispatch<IApplicationState, IApplicationContext, TReduxActions>;
}

/**
 * Хелпер для создания Redux стора
 * @param initialState IApplicationState - предзагруженное состояние
 */
export function createReduxStore(
  context: IApplicationContext,
  initialState: Partial<IApplicationState>,
  debugEnabled: boolean,
) {
  const reducers = combineReducers<IApplicationState, TReduxActions>({
    ...oldReducers,
    geoSwitcher: geoSwitcherReducer,
    filters: combineReducers<IFiltersState, TReduxActions>({
      jsonQuery: reduceReducers<IJsonQuery, TReduxActions>(
        setJsonQueryReducer,
        setDealTypeReducer,
        selectOfferTypeReducer,
        selectAllOfferTypesReducer,
        setPriceReducer,
        selectGeoReducer,
        clearGeoReducer,
        setRegionReducer,
        /** Всегда последний */
        clearReducer,
        setVillageReducer,
        setBSCentersReducer,
        setBuilderReducer,
        setCoworkingIdReducer,
      ),
      meta: metaReducer,
      suggestions: suggestionsReducer,
      geoSearch: geoSearchReducer,
      clientErrors: clientErrorsReducer,
      geocode: geocodeReducer,
    }),
    experiments: experimentsReducer,
    tracking: trackingReducer,
    suburbanRk: createSimpleReducer(null),
    user: createSimpleReducer<IUser>({}),
    adfox: adfoxReducer,
  });

  const composeEnhancers = debugEnabled ? window.__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose : compose;

  const storeEnchancers = composeEnhancers(applyMiddleware(thunk.withExtraArgument(context)));

  const createStore = (): IReduxStore =>
    _createStore<IApplicationState, TReduxActions, IThunkExt, {}>(
      reducers,
      initialState as IApplicationState,
      storeEnchancers,
    );

  if (process.env.NODE_ENV === 'development' && debugEnabled) {
    if (!storeReminder) {
      storeReminder = createStore();
    }

    storeReminder.replaceReducer(reducers);

    return storeReminder;
  }

  return createStore();
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function reduceReducers<S, A extends Action<any> = AnyAction>(...reducers: Reducer<S, A>[]): Reducer<S, A> {
  return (prevState: S | undefined, action: A): S =>
    reducers.reduce((newState, reducer) => reducer(newState, action), prevState) as S;
}
